<template>
  <div class="trialModal">
    <div class="iconContainer">
      <Icon
        icon="premiumDiscover"
        :scale="3"
      />
    </div>

    <ModalHeader
      :centered="true"
      class="modalSectionHeader"
      :data-test-id="$testId('modalHeader')"
      :headerSubtext="subHeaderText"
      :headerText="headerText"
      :showClose="false"
    />

    <Card
      class="trialInfoCard"
      :hasNoBorder="true"
    >
      <div class="infoColumn">
        <TrialFeatureInfo
          :data-test-id="$testId('trialFeatureInfo')"
          :featureCode="featureCode"
          :showPricing="lessThanFifteenDaysLeft"
        />
      </div>
      <div class="infoColumn">
        <div class="columnWrapper">
          <FairUseCountdown
            :data-test-id="$testId('fairUseCountdown')"
            :endDate="trialData.trialEndDate"
            :featureName="featureName"
            :startDate="trialData.trialStartDate"
            @close="triggerClose"
          />

          <FollowUp
            class="followUp"
            :data-test-id="$testId('followUp')"
            :featureCode="featureCode"
            :showNextSteps="lessThanFifteenDaysLeft"
            @close="triggerClose"
          />
        </div>
      </div>
    </Card>

    <ParagraphBody
      class="infoSection"
    >
      <FairUseDetails
        :data-test-id="$testId('fairUseDetails')"
        :endDate="trialData.trialEndDate"
        :featureName="featureName"
        :recommendationPackageName="featurePackage"
        @close="triggerClose"
      />
    </ParagraphBody>

    <ParagraphBody class="footer">
      <Button
        :data-test-id="$testId('acceptButton')"
        :hasOutline="lessThanFifteenDaysLeft"
        text="Got It"
        :type="lessThanFifteenDaysLeft ? 'secondary' : 'primary'"
        @click="handleAccept"
      />
    </ParagraphBody>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import {
  Button,
  Card,
  Icon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import moment from 'moment';

import { FeatureTrialTextConsts } from 'client/util/Constants/FeatureTrialConstants';

import EventHelper from 'client/GrowthEngineering/EventHelper';
import FairUseCountdown from 'client/components/Modal/TrialModal/FairUseCountdown.vue';
import FairUseDetails from 'client/components/Modal/TrialModal/FairUseDetails.vue';
import FollowUp from 'client/components/Modal/TrialModal/FollowUp.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import TrialFeatureInfo from 'client/components/Modal/TrialModal/TrialFeatureInfo.vue';

export default {
  name: 'TrialModalV2',

  components: {
    Button,
    Card,
    FairUseDetails,
    Icon,
    ModalHeader,
    ParagraphBody,
    FairUseCountdown,
    FollowUp,
    TrialFeatureInfo,
  },

  props: {
    featureCode: {
      type: String,
      required: true,
    },
    featureName: {
      type: String,
      required: true,
    },
    featurePackage: {
      type: String,
      default: () => '',
    },
    isAddOn: {
      type: Boolean,
    },
    showWelcomeMessage: {
      type: Boolean,
      default: () => true,
    },
    subHeaderText: {
      type: String,
      default: () => 'This feature is not included in your current package.',
    },
  },

  computed: {
    ...mapGetters('FeatureTrialModule', [
      'getFeatureUsage',
    ]),

    ...mapState('SettingsModule', [
      'orgId',
    ]),

    featureUsage() {
      return this.getFeatureUsage(this.featureCode);
    },

    headerText() {
      return this.showWelcomeMessage
        ? `Welcome to ${this.featureName} Trial!`
        : `You are trying ${this.featureName}.`;
    },

    lessThanFifteenDaysLeft() {
      return this.trialData.trialEndDate.diff(moment().endOf('day'), 'days') <= 15;
    },

    trialData() {
      return {
        trialStartDate: moment(this.featureUsage.startDate),
        trialEndDate: moment(this.featureUsage.startDate).add(30, 'days').endOf('day'),
      };
    },
  },

  EventHelper,
  ModalEventBus,

  created() {
    UiEvents.triggerModalViewed({
      description: `${this.featureName} Trial Modal Viewed`,
      fromUrl: this.$route.path,
      page: `${this.featureName} Trial Modal`,
      section: `'${this.featureName} page header`,
    });

    this.fetchAccountManagerCalendarUrl(this.orgId);

    this.FEATURE_TRIAL_TEXT_CONSTS = FeatureTrialTextConsts[this.featureCode];
  },

  methods: {
    ...mapActions('AccountManagerCalendarModule', [
      'fetchAccountManagerCalendarUrl',
    ]),

    ...mapActions('FeatureTrialModule', [
      'addFeatureUse',
    ]),

    handleAccept() {
      UiEvents.triggerButtonClicked({
        description: 'Got It Button Clicked',
        section: 'footer',
        text: 'Got it!',
        page: `${this.featureName} Trial Modal`,
      });

      this.triggerClose();
    },

    triggerClose() {
      ModalEventBus.close();
    },
  },
};
</script>

<style scoped>

.infoColumn {
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 0 46px;
  position: relative;
}

.followUp {
  padding-top: var(--jc-spacer-large);
}

.footer {
  text-align: center;
}

.footer a {
  color: var(--jc-color-surface-strong);
  font-weight: normal;
  text-decoration: underline;
}

.iconContainer {
  align-items: center;
  background-color: var(--jcTertiary50);
  border-radius: 50%;
  display: flex;
  height: 74px;
  justify-content: center;
  margin: 0 auto;
  width: 74px;
}

.infoSection {
  margin-top: var(--jc-spacer-medium);
  text-align: left;
}

.infoSection h4 {
  font-size: var(--jcBodyLarge);
}

.trialModal .modalSectionHeader {
  flex-direction: column;
}

.trialModal .modalSectionHeader /deep/ p {
  font-size: var(--jcBody);
  padding-bottom: var(--jc-spacer-medium);
}

.modalSectionHeader /deep/ h1 {
  font-weight: normal;
}

.trialModal {
  padding: 36px 106px 16px;
}

.trialInfoCard {
  --card-background: var(--jcTertiary50);

  display: flex;
  flex-wrap: wrap;
  padding: var(--jc-spacer-medium);
}

@media (min-width: 768px) {
  .column:not(:first-child)::before {
    background: var(--jc-color-neutral-stroke);
    content: '';
    height: 95%;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
  }
}
</style>
